<script setup lang="ts">
/**
 * The live approval dialog of remote support (Fernwartung): „BLP-Support
 * möchte sich aufschalten“. It opens at once for a holder of
 * `master.support.approve` when a request arrives (the store reads it from the
 * support route, pushed by master's notification) and shows the oldest waiting
 * request; the next one follows when it is decided. The admin approves with the
 * duration asked for or a shorter step, or turns the request down. A request
 * that disappears — withdrawn, expired, decided in another tab — closes its
 * dialog by itself; closing it with „x“ only hides it (it stays open for the
 * other admins).
 */
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import {
  NxBanner,
  NxButton,
  NxKeyValueList,
  NxLabel,
  NxModal,
  NxNativeSelect,
  formatRelativeTime,
  type NxKeyValueItem,
} from '@blpde/nx-ui';
import { approvableMinutes, supportDurationLabel } from '@/support/durations';
import { useSupportRequests } from '@/support/useSupportRequests';

const { t, locale } = useI18n();
const { current, busy, error, approve, deny, dismiss, clearError } = useSupportRequests();

const minutes = ref<string | number | null>(null);

// Every request starts at the duration asked for.
watch(
  () => current.value?.id,
  () => {
    minutes.value = current.value?.requestedMinutes
      ? approvableMinutes(current.value.requestedMinutes).at(-1)!
      : null;
    clearError();
  },
  { immediate: true }
);

const open = computed({
  get: () => current.value !== null,
  set: (value: boolean) => {
    if (!value && current.value) dismiss(current.value.id);
  },
});

const subtitle = computed(() =>
  t('connect.support.dialog.subtitle', {
    time: formatRelativeTime(current.value?.requestedAt, { locale: locale.value }),
  })
);

const facts = computed<NxKeyValueItem[]>(() => {
  const request = current.value;
  if (!request) return [];
  return [
    { key: 'name', label: t('connect.support.dialog.name'), value: request.operatorName },
    { key: 'email', label: t('connect.support.dialog.email'), value: request.operatorEmail },
    { key: 'reason', label: t('connect.support.dialog.reason'), value: request.reason, full: true },
    {
      key: 'requested',
      label: t('connect.support.dialog.requested'),
      value: supportDurationLabel(request.requestedMinutes, t, locale.value),
    },
  ];
});

const options = computed(() =>
  approvableMinutes(current.value?.requestedMinutes ?? 0).map((step) => ({
    value: step,
    label: supportDurationLabel(step, t, locale.value),
  }))
);

async function onApprove(): Promise<void> {
  const request = current.value;
  if (!request || minutes.value === null) return;
  await approve(request.id, Number(minutes.value));
}

async function onDeny(): Promise<void> {
  const request = current.value;
  if (request) await deny(request.id);
}
</script>

<template>
  <NxModal
    v-model:open="open"
    :title="t('connect.support.dialog.title')"
    :subtitle="subtitle"
    :close-on-click-outside="false"
    data-testid="support-approval-dialog"
  >
    <template #banner>
      <NxBanner v-if="error" variant="danger" role="alert">{{ t(error) }}</NxBanner>
    </template>
    <template #body>
      <div v-if="current" class="cx-support-approval">
        <NxKeyValueList layout="stacked" :items="facts" />
        <NxLabel :text="t('connect.support.dialog.grantFor')">
          <NxNativeSelect
            v-model="minutes"
            :options="options"
            data-testid="support-approval-minutes"
          />
        </NxLabel>
        <NxBanner variant="info" :title="t('connect.support.dialog.meaning.title')">
          <ul class="cx-support-approval-meaning">
            <li>{{ t('connect.support.dialog.meaning.rights') }}</li>
            <li>{{ t('connect.support.dialog.meaning.ends') }}</li>
            <li>{{ t('connect.support.dialog.meaning.end') }}</li>
            <li>{{ t('connect.support.dialog.meaning.log') }}</li>
          </ul>
        </NxBanner>
      </div>
    </template>
    <template #footer>
      <NxButton variant="outline" type="button" :disabled="busy" @click="onDeny">
        {{ t('connect.support.dialog.deny') }}
      </NxButton>
      <NxButton type="button" :disabled="busy" @click="onApprove">
        {{ t('connect.support.dialog.approve') }}
      </NxButton>
    </template>
  </NxModal>
</template>

<style scoped>
.cx-support-approval {
  display: flex;
  flex-direction: column;
  gap: var(--nxt-space-md);
}

.cx-support-approval-meaning {
  margin: 0;
  padding-left: var(--nxt-space-md);
}
</style>
